/**
 * jsPdfBranding — adds branded header & footer to jsPDF documents.
 * Call addBrandedHeader(doc, options) after creating the jsPDF instance.
 * Call addBrandedFooter(doc) before saving.
 *
 * Returns the Y position after the header so you know where to start content.
 */

const BRAND_DARK = "#1D2939";
const BRAND_AMBER = "#E6A817";
const BRAND_MUTED = "#777777";
const BRAND_BODY = "#1a1a1a";
const BRAND_LIGHT = "#cccccc";

/**
 * Draws a branded header on the current page.
 * @param {jsPDF} doc
 * @param {Object} opts
 * @param {string} opts.facilityName
 * @param {string} opts.reportTitle   – e.g. "Notes Report"
 * @param {string} opts.reportSubtitle – e.g. "Jan 2026 - Mar 2026"
 * @param {string} opts.generatedDate – e.g. "03/26/2026"
 * @param {number} [opts.startX=30]   – left margin
 * @param {number} [opts.startY=20]   – top margin
 * @param {number} [opts.pageWidth]   – page width in pt (auto-detected)
 * @returns {number} nextY – Y position after header (where content should start)
 */
export const addBrandedHeader = (doc, opts = {}) => {
  const {
    facilityName = "Facility",
    reportTitle = "Report",
    reportSubtitle = "",
    generatedDate = new Date().toLocaleDateString("en-US"),
    startX = 30,
    startY = 20,
  } = opts;

  const pageWidth =
    opts.pageWidth || doc.internal.pageSize.getWidth();

  // Amber logo circle
  doc.setFillColor(BRAND_AMBER);
  doc.circle(startX + 12, startY + 12, 12, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor("#ffffff");
  doc.text(facilityName[0] || "O", startX + 12, startY + 16, { align: "center" });

  // Facility name
  doc.setTextColor(BRAND_BODY);
  doc.setFontSize(12);
  doc.text(facilityName, startX + 30, startY + 10);

  // Report subtitle under facility
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(BRAND_MUTED);
  doc.text(reportTitle, startX + 30, startY + 20);

  // Right side — generated date
  doc.setFontSize(8);
  doc.setTextColor(BRAND_MUTED);
  const rightText = `${reportSubtitle ? reportSubtitle + "  ·  " : ""}Generated ${generatedDate}`;
  doc.text(rightText, pageWidth - startX, startY + 10, { align: "right" });

  // Divider line
  const lineY = startY + 28;
  doc.setDrawColor(BRAND_BODY);
  doc.setLineWidth(1.5);
  doc.line(startX, lineY, pageWidth - startX, lineY);

  return lineY + 12; // Next Y for content
};

/**
 * Adds a branded footer to every page.
 * Call BEFORE doc.save().
 * @param {jsPDF} doc
 * @param {number} [marginX=30]
 */
export const addBrandedFooter = (doc, marginX = 30) => {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerY = pageHeight - 20;

    // Top border
    doc.setDrawColor(BRAND_LIGHT);
    doc.setLineWidth(0.5);
    doc.line(marginX, footerY - 5, pageWidth - marginX, footerY - 5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(BRAND_MUTED);
    doc.text("Confidential — Report generated by Ogiso", marginX, footerY);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, footerY, {
      align: "right",
    });
  }
};
